import React from "react";
import { CheckCircle2, CircleDashed, Clock } from "lucide-react";
import { api } from "@/lib/api";

const STATUS = {
  complete:    { label: "Complete",    Icon: CheckCircle2, pill: "bg-green-700/10 text-green-800 border-green-700/30" },
  in_progress: { label: "In Progress", Icon: Clock,        pill: "bg-aviana-gold/10 text-aviana-ink border-aviana-gold/40" },
  not_started: { label: "Not Started", Icon: CircleDashed, pill: "bg-white text-aviana-muted border-aviana-border" },
};

export function StatusPill({ status, testid }) {
  const s = STATUS[status] || STATUS.not_started;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] tracking-[0.14em] uppercase whitespace-nowrap ${s.pill}`} data-testid={testid}>
      <s.Icon size={12} /> {s.label}
    </span>
  );
}

function Requirements({ reqs }) {
  return (
    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1">
      {reqs.map((r) => (
        <span key={r.label} className={`text-[12px] ${r.done ? "text-green-800" : "text-aviana-muted"}`}>
          {r.label}: {r.state || <strong className="font-medium">{r.value}/{r.target}</strong>}
        </span>
      ))}
    </div>
  );
}

export function ProgressBar({ percent, testid }) {
  return (
    <div className="flex items-center gap-4" data-testid={testid}>
      <div className="flex-1 h-2.5 rounded-full bg-aviana-border overflow-hidden">
        <div className="h-full bg-aviana-gold transition-[width] duration-700" style={{ width: `${percent}%` }} />
      </div>
      <span className="font-display text-2xl text-aviana-ink tabular-nums w-14 text-right">{percent}%</span>
    </div>
  );
}

// Per-module tracker (status computed server-side). `compact` hides requirement details.
export function ModuleProgressTracker({ progress, compact = false, testid = "module-progress" }) {
  if (!progress) return null;
  return (
    <div data-testid={testid}>
      <ProgressBar percent={progress.percent} testid={`${testid}-percent`} />
      <p className="mt-1.5 text-[11px] tracking-[0.16em] uppercase text-aviana-muted">
        {progress.complete} of {progress.total} modules complete
      </p>
      <ul className="mt-5 divide-y divide-aviana-border/60">
        {progress.modules.map((m) => (
          <li key={m.key} className="py-3 flex items-start justify-between gap-4" data-testid={`${testid}-module-${m.key}`}>
            <div className="min-w-0">
              <p className="text-[14px] text-aviana-ink">{m.label}</p>
              {!compact && m.requirements && <Requirements reqs={m.requirements} />}
              {!compact && m.detail && <p className="mt-0.5 text-[12px] text-aviana-muted">{m.detail}</p>}
            </div>
            <StatusPill status={m.status} testid={`${testid}-status-${m.key}`} />
          </li>
        ))}
      </ul>
    </div>
  );
}

export default ModuleProgressTracker;

// Course-page panel: overall % always visible, module list expandable. Refetches when `refreshKey` changes.
export function CourseProgressPanel({ courseWpId, refreshKey }) {
  const [progress, setProgress] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => {
    api.get("/lms/my-module-progress")
      .then(({ data }) => setProgress(data.find((p) => String(p.course.wp_post_id) === String(courseWpId)) || null))
      .catch(() => setProgress(null));
  }, [courseWpId, refreshKey]);
  if (!progress) return null;
  return (
    <section className="bg-aviana-cream rounded-2xl border border-aviana-border px-6 py-5 mb-10" data-testid="course-progress-panel">
      <div className="flex items-center justify-between gap-4 mb-3">
        <p className="overline-plum">Your progress</p>
        <button onClick={() => setOpen((o) => !o)} className="text-[11px] tracking-[0.18em] uppercase text-aviana-muted hover:text-aviana-plum transition-colors" data-testid="course-progress-toggle">
          {open ? "Hide modules" : "Show modules"}
        </button>
      </div>
      {open ? (
        <ModuleProgressTracker progress={progress} testid="course-module-progress" />
      ) : (
        <>
          <ProgressBar percent={progress.percent} testid="course-progress-percent" />
          <p className="mt-1.5 text-[11px] tracking-[0.16em] uppercase text-aviana-muted" data-testid="course-progress-count">
            {progress.complete} of {progress.total} modules complete
          </p>
        </>
      )}
    </section>
  );
}
